// PHASE_1 / B2 — time/attendance feature API helpers (punch + entries +
// timesheet transitions + schedules/shifts).
//
// The platform-graph time routes validate their narrow header contracts, but
// their full query/response schemas are not declared yet. Each call is cast to
// its verified contract via the SAME `platformGraph.<VERB> as unknown as
// <Typed>` pattern B0 established for the GET reference slice (tightens
// automatically when full route schemas land and the types regenerate).
//
// Hours are DECIMALS (`_hours`), never money — nothing here goes through <Money>
// (Phase 1 money/hours boundary).

import type { GeofencePoint, GeofenceSummary } from "@novemai/time-attendance-sdk";
import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";
import type { TimesheetDaysView } from "./timesheet-days-contract";

export type { TimesheetDayLeave, TimesheetDay, TimesheetDaysView } from "./timesheet-days-contract";

/** A time-entry row the API returns (documented shape from routes/time-attendance.ts). */
export interface TimeEntryRow {
  readonly entry_id: string;
  readonly tenant_id: string;
  readonly employee_id: string;
  readonly employment_id: string;
  readonly kind: string;
  readonly start_at: string;
  readonly end_at: string | null;
  readonly duration_minutes: number | null;
  readonly source: string;
  readonly status: string;
  readonly pay_code: string | null;
  readonly timesheet_id: string | null;
  /** ADR-018 integrity outcome, rendered only in manager review context. */
  readonly integrity_verdict: string | null;
  /** Opaque integrity signal bundle; never rendered by this client. */
  readonly integrity_signals: unknown | null;
  /** Server-owned manager quality-gate marker. */
  readonly requires_review: boolean | null;
  readonly geofence_match?: GeofenceSummary | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  /**
   * Tombstone-gated display name, resolved server-side on the LIST route only
   * (ADR-011). Null on single-entry responses and for redacted subjects — the
   * review queue falls back to the id prefix rather than inventing a label.
   */
  readonly employee_name: string | null;
}

/** A timesheet row from GET /v1/time/timesheets. Decimal hours, never money. */
export interface TimesheetRow {
  readonly timesheet_id: string;
  readonly tenant_id: string;
  readonly employee_id: string;
  readonly employment_id: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly total_regular_hours: string;
  readonly total_ot_hours: string;
  readonly total_double_time_hours: string;
  readonly total_holiday_hours: string;
  readonly total_pto_hours: string;
  readonly total_unpaid_hours: string;
  /**
   * True until the sheet is locked: the six totals above are then the numbers
   * lock would persist right now (approved leave included), not final figures.
   */
  readonly provisional: boolean;
  /** Days on this sheet where the person punched despite approved leave. */
  readonly leave_day_worked_days: number;
  readonly status: string;
  readonly locked_at: string | null;
  /**
   * Tombstone-gated display name resolved server-side on the LIST route only
   * (ADR-011). Null for a redacted subject, in which case the queue falls back
   * to the id prefix rather than inventing a label. Absent on transition
   * responses, which return the bare row.
   */
  readonly employee_name?: string | null;
}

/** A structural schedule row from GET /v1/time/schedules. */
export interface ScheduleRow {
  readonly schedule_id: string;
  readonly tenant_id: string;
  readonly name: string;
  readonly scope_kind: string;
  readonly scope_id: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly status: string;
  readonly published_at: string | null;
  readonly published_by: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

/**
 * SEM-7206. The list route's own status filter. Filtering client-side would
 * read the loaded keyset pages only — a tenant past one page would be told it
 * has no drafts while its drafts sat on page two.
 */
export type ScheduleStatus = "draft" | "published" | "archived";

/** The schedule scope kinds the route's CreateScheduleBodySchema accepts. */
export type ScheduleScopeKind = "team" | "location" | "department" | "legal_entity";

/** POST /v1/time/schedules — the route always lands the row at `draft`. */
export interface CreateScheduleInput {
  readonly name: string;
  readonly scope_kind: ScheduleScopeKind;
  readonly scope_id: string;
  /** Date-only ISO string ("2026-09-01"); the route rejects a reversed period. */
  readonly period_start: string;
  readonly period_end: string;
}

/** A shift row from GET /v1/time/shifts (the documented ShiftResponseRow). */
export interface ShiftRow {
  readonly shift_id: string;
  readonly tenant_id: string;
  readonly schedule_id: string;
  readonly assigned_employee_id: string | null;
  readonly role: string | null;
  readonly start_at: string;
  readonly end_at: string;
  readonly status: string;
  readonly created_at: string | null;
}

/**
 * POST /v1/time/shifts. `assigned_employee_id` is what decides the row's
 * initial status (`assigned` vs `open`) — there is no separate assign route, so
 * omitting it here is the only way to author an open roster slot, and supplying
 * it later is impossible.
 */
export interface CreateShiftInput {
  readonly schedule_id: string;
  readonly start_at: string;
  readonly end_at: string;
  readonly timezone: string;
  readonly assigned_employee_id?: string;
  readonly role?: string;
}

/**
 * POST /v1/time/schedules/{id}/shift-patterns (SEM-8097). No dates: the
 * schedule's own period bounds the expansion, so the client cannot widen it.
 * Draft schedules only — the server answers 409 on a published one.
 */
export interface ShiftPatternInput {
  readonly rrule: string;
  /** "HH:MM" wall clock in `timezone`, not an instant (DST-safe). */
  readonly start_time_local: string;
  readonly end_time_local: string;
  readonly timezone: string;
  readonly role?: string;
}

/** The pattern row the create call echoes back, with the row count it wrote. */
export interface ShiftPatternRow {
  readonly pattern_id: string;
  readonly tenant_id: string;
  readonly schedule_id: string;
  readonly rrule: string;
  readonly start_time_local: string;
  readonly end_time_local: string;
  readonly timezone: string;
  readonly role: string | null;
  readonly created_at: string | null;
}

/** The per-day overtime split the lock response returns (decimal hours). */
export interface OvertimePayload {
  readonly applied_rule: string;
  readonly jurisdiction: string;
  readonly regular_hours: number;
  readonly ot1_hours: number;
  readonly ot2_hours: number;
  readonly per_day: readonly {
    readonly date: string;
    readonly regular_hours: number;
    readonly ot1_hours: number;
    readonly ot2_hours: number;
  }[];
}

/** The punch toggle response — the entry + which side of the toggle fired. */
export interface PunchResult {
  readonly entry: TimeEntryRow;
  readonly action: "clock_in" | "clock_out";
}

/** The punch body — Phase 1 accepts source=web|manager_entry only. */
export interface PunchInput {
  readonly source: "web" | "manager_entry";
  /** Optional server-side intent guard. The web Damga flow pins this from its
   * authoritative active-entry lookup so a response-loss retry cannot turn a
   * clock-in into a clock-out. */
  readonly direction?: "in" | "out";
  readonly timezone: string;
  readonly at?: string;
  readonly employee_id?: string;
  readonly notes?: string;
}

// ---- typed-cast call signatures (full route schemas are still pending) ----

type PostPunch = (
  path: "/v1/time/punch",
  init: { body: PunchInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<PunchResult>>;

type GetEntries = (
  path: "/v1/time/entries",
  init: {
    params: {
      query: {
        cursor?: string;
        status?: string;
        limit?: number;
        requires_review?: boolean;
        employee_id?: string;
        source?: string;
        order?: "created_asc" | "start_desc";
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<TimeEntryRow>>>;

/** api_time_approve_entry has no body: path id + caller-retained key only. */
type PostEntryApproval = (
  path: "/v1/time/entries/{id}/approve",
  init: {
    params: { path: { id: string } };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ entry: TimeEntryRow }>>;

type PatchEntry = (
  path: "/v1/time/entries/{id}",
  init: {
    params: { path: { id: string } };
    body: TimeEntryCorrectionInput;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ entry: TimeEntryRow }>>;

type GetTimesheets = (
  path: "/v1/time/timesheets",
  init: { params: { query: { cursor?: string; status?: string; employee_id?: string; limit?: number } } },
) => Promise<EnvelopedFetchResult<KeysetPage<TimesheetRow>>>;

/** An offline punch the revocation gate refused (ADR-019), awaiting a human. */
export interface OrphanedPunchRow {
  readonly orphaned_punch_id: string;
  readonly tenant_id: string;
  readonly device_id: string;
  readonly employee_id: string;
  readonly kind: string;
  readonly claimed_at: string;
  readonly claimed_lat: string | null;
  readonly claimed_lng: string | null;
  readonly geofence_state: string | null;
  readonly refused_reason: string;
  readonly reconcile_status: string;
  readonly reconciled_by: string | null;
  readonly accepted_time_entry_id: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

type GetOrphanedPunches = (
  path: "/v1/time/orphaned-punches",
  init: { params: { query: { cursor?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<OrphanedPunchRow>>>;

type PostOrphanedPunchDecision = (
  path: "/v1/time/orphaned-punches/{id}/accept" | "/v1/time/orphaned-punches/{id}/dismiss",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<{ orphaned_punch: OrphanedPunchRow }>>;

/** The timesheet statuses GET /v1/time/timesheets filters on server-side. */
export type TimesheetStatus = "open" | "submitted" | "approved" | "locked" | "reopened";

/** The entry statuses ListEntriesQuerySchema accepts. */
export const TIME_ENTRY_STATUSES = [
  "pending",
  "active",
  "submitted",
  "approved",
  "locked",
  "rejected",
  "adjusted",
  "voided",
] as const;
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number];

/** The punch sources entry rows carry (each has a time.source.* label). */
export const TIME_ENTRY_SOURCES = ["web", "mobile", "kiosk", "manager_entry", "api", "import"] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

export interface PunchFeedFilters {
  readonly employeeId?: string;
  readonly status?: TimeEntryStatus;
  readonly source?: TimeEntrySource;
}

type GetTimesheetDays = (
  path: "/v1/time/timesheets/{id}/days",
  init: { params: { path: { id: string } } },
) => Promise<EnvelopedFetchResult<TimesheetDaysView>>;

type GetWeekDays = (
  path: "/v1/time/timesheet-days",
  init: { params: { query: { week_start: string; employee_id?: string } } },
) => Promise<EnvelopedFetchResult<TimesheetDaysView>>;

type GetSchedules = (
  path: "/v1/time/schedules",
  init: { params: { query: { cursor?: string; status?: ScheduleStatus } } },
) => Promise<EnvelopedFetchResult<KeysetPage<ScheduleRow>>>;

type PostSchedule = (
  path: "/v1/time/schedules",
  init: { body: CreateScheduleInput },
) => Promise<EnvelopedFetchResult<{ schedule: ScheduleRow }>>;

type GetShifts = (
  path: "/v1/time/shifts",
  init: {
    params: {
      query: {
        schedule_id?: string;
        assigned_employee_id?: string;
        schedule_status?: "draft" | "published" | "archived";
        ends_after?: string;
        cursor?: string;
      };
    };
  },
) => Promise<EnvelopedFetchResult<KeysetPage<ShiftRow>>>;

type PostShiftPattern = (
  path: "/v1/time/schedules/{id}/shift-patterns",
  init: {
    params: { path: { id: string } };
    body: ShiftPatternInput;
    headers: Record<string, string>;
  },
) => Promise<
  EnvelopedFetchResult<{ pattern: ShiftPatternRow; shift_count: number }>
>;

type PostShift = (
  path: "/v1/time/shifts",
  init: { body: CreateShiftInput },
) => Promise<EnvelopedFetchResult<{ shift: ShiftRow }>>;

/** api_time_publish_schedule is bodyless: id + caller-retained key only. */
type PostSchedulePublish = (
  path: "/v1/time/schedules/{id}/publish",
  init: {
    params: { path: { id: string } };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ schedule: ScheduleRow }>>;

type PostTimesheetTransition = (
  path: string,
  init: {
    params: { path: { id: string } };
    body?: Record<string, unknown>;
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ timesheet: TimesheetRow }>>;

type PostTimesheetLock = (
  path: "/v1/time/timesheets/{id}/lock",
  init: {
    params: { path: { id: string } };
    body?: { jurisdiction?: string };
    headers: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ timesheet: TimesheetRow; overtime: OvertimePayload }>>;

// ---- mutation + query functions -------------------------------------------

// SEM-2008 (H6): POST /v1/time/punch hard-requires x-idempotency-key server-side
// (requireIdempotencyKey in routes/time-attendance.ts) — a network retry of the
// SAME punch must collapse to one result instead of registering a second clock
// event. The caller owns the key because only the UI can distinguish a retry of
// one intent from a later, distinct punch.
export async function punch(input: PunchInput, idempotencyKey: string): Promise<PunchResult> {
  const post = platformGraph.POST as unknown as PostPunch;
  return unwrapEnvelope(
    post("/v1/time/punch", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * The caller's OWN entry list. `employeeId` is REQUIRED, not optional: the
 * route narrows to the caller only for `employee_self` and answers the whole
 * tenant to `executive_broad` and the caller's subtree to `manager_subtree`, so
 * a self page that omits the id shows a broad-scope user other people's rows as
 * their own (E22). Making the id a positional argument means the next self-page
 * call cannot forget it. The server already ANDs `employee_id` with its own
 * scope, so passing one's own id never widens anything.
 *
 * Newest first, decided by the server (`order=start_desc`) rather than by
 * sorting the loaded window: a client-side sort would only reorder page one,
 * and page one is the OLDEST fifty rows in the default order.
 */
export async function fetchOwnTimeEntriesPage(
  employeeId: string,
  cursor: string | null,
): Promise<KeysetPage<TimeEntryRow>> {
  const get = platformGraph.GET as unknown as GetEntries;
  return unwrapEnvelope(
    get("/v1/time/entries", {
      params: {
        query: {
          employee_id: employeeId,
          order: "start_desc",
          ...(cursor !== null && { cursor }),
        },
      },
    }),
  );
}

/**
 * Manager/admin review queue. `requires_review` stays in every keyset request
 * so pagination never silently broadens into the caller's general entry list.
 * Scope narrowing is authoritative on the server (employee-self/subtree/
 * executive); this client deliberately sends no employee identifier.
 */
export async function fetchReviewEntriesPage(
  cursor: string | null,
): Promise<KeysetPage<TimeEntryRow>> {
  const get = platformGraph.GET as unknown as GetEntries;
  return unwrapEnvelope(
    get("/v1/time/entries", {
      params: {
        query: {
          requires_review: true,
          ...(cursor !== null && { cursor }),
        },
      },
    }),
  );
}

/**
 * The entry-level quality gate is intentionally a bodyless route. The caller
 * owns the key across an ambiguous response so retry replays the SAME approval;
 * actor and self-exclusion are resolved and enforced by the backend.
 */
export async function approveTimeEntry(
  id: string,
  idempotencyKey: string,
): Promise<{ entry: TimeEntryRow }> {
  const post = platformGraph.POST as unknown as PostEntryApproval;
  return unwrapEnvelope(
    post("/v1/time/entries/{id}/approve", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * api_time_edit_entry body as this client sends it. The spec request shape
 * names `edit_reason` without `?`, so the client always sends one even though
 * the route's zod schema still accepts it as optional. `breaks` is in the spec
 * shape but not in the route schema, so it is not offered.
 */
export interface TimeEntryCorrectionInput {
  readonly start_at?: string;
  readonly end_at?: string;
  readonly edit_reason: string;
}

/** Manager correction of one entry; the caller retains the key across a lost response. */
export async function correctTimeEntry(
  id: string,
  body: TimeEntryCorrectionInput,
  idempotencyKey: string,
): Promise<{ entry: TimeEntryRow }> {
  const patch = platformGraph.PATCH as unknown as PatchEntry;
  return unwrapEnvelope(
    patch("/v1/time/entries/{id}", {
      params: { path: { id } },
      body,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * The caller's open (`active`, no end_at) punch, if any — an AUTHORITATIVE
 * single-row lookup (`status=active&limit=1`) rather than scanning the first
 * keyset page. The clock toggle reads this so an on-the-clock state is correct
 * even when the open punch would fall off-page in the paginated entry list.
 *
 * Pinned to `employeeId` for the reason on fetchOwnTimeEntriesPage: without it
 * a broad-scope caller's `limit=1` lands on whichever colleague is clocked in,
 * and the toggle then points the wrong way.
 */
export async function fetchOwnActiveEntry(employeeId: string): Promise<TimeEntryRow | null> {
  const get = platformGraph.GET as unknown as GetEntries;
  const page = await unwrapEnvelope(
    get("/v1/time/entries", {
      params: { query: { employee_id: employeeId, status: "active", limit: 1 } },
    }),
  );
  return page.items[0] ?? null;
}

/**
 * The status rides on every page request: filtering the loaded window instead
 * would tell a tenant past one page that it has no submitted sheets while they
 * sat on page two.
 */
export async function fetchTimesheetsPage(
  cursor: string | null,
  filter: { readonly status?: TimesheetStatus; readonly limit?: number } = {},
): Promise<KeysetPage<TimesheetRow>> {
  const get = platformGraph.GET as unknown as GetTimesheets;
  return unwrapEnvelope(
    get("/v1/time/timesheets", {
      params: { query: { ...filter, ...(cursor !== null && { cursor }) } },
    }),
  );
}

/**
 * The manager punch feed, newest first. The route has no date-range filter, so
 * this is "the latest records" in the caller's scope, never "today"; the server
 * narrows the rows to the caller's tier.
 */
export async function fetchPunchFeedPage(
  filters: PunchFeedFilters,
  cursor: string | null,
): Promise<KeysetPage<TimeEntryRow>> {
  const get = platformGraph.GET as unknown as GetEntries;
  return unwrapEnvelope(
    get("/v1/time/entries", {
      params: {
        query: {
          order: "start_desc",
          ...(filters.employeeId !== undefined && { employee_id: filters.employeeId }),
          ...(filters.status !== undefined && { status: filters.status }),
          ...(filters.source !== undefined && { source: filters.source }),
          ...(cursor !== null && { cursor }),
        },
      },
    }),
  );
}

/** The pending reconcile queue (the route's default status). */
export async function fetchOrphanedPunchesPage(
  cursor: string | null,
): Promise<KeysetPage<OrphanedPunchRow>> {
  const get = platformGraph.GET as unknown as GetOrphanedPunches;
  return unwrapEnvelope(
    get("/v1/time/orphaned-punches", {
      params: { query: cursor !== null ? { cursor } : {} },
    }),
  );
}

/**
 * Both decisions are bodyless and carry no idempotency key: the server flips
 * pending_reconcile under its own state guard, so a replay after a lost
 * response answers 409 time.orphan_not_pending instead of writing twice.
 */
export async function decideOrphanedPunch(
  id: string,
  decision: "accept" | "dismiss",
): Promise<OrphanedPunchRow> {
  const post = platformGraph.POST as unknown as PostOrphanedPunchDecision;
  const result = await unwrapEnvelope(
    post(
      decision === "accept"
        ? "/v1/time/orphaned-punches/{id}/accept"
        : "/v1/time/orphaned-punches/{id}/dismiss",
      { params: { path: { id } } },
    ),
  );
  return result.orphaned_punch;
}

/**
 * The caller's OWN timesheets. `employeeId` is required for the reason on
 * fetchOwnTimeEntriesPage: only employee_self is narrowed to its own rows by
 * the server, so a broad-scope caller's "my timesheets" would otherwise list
 * the whole tenant's.
 */
export async function fetchOwnTimesheetsPage(
  employeeId: string,
  cursor: string | null,
): Promise<KeysetPage<TimesheetRow>> {
  const get = platformGraph.GET as unknown as GetTimesheets;
  return unwrapEnvelope(
    get("/v1/time/timesheets", {
      params: { query: { employee_id: employeeId, ...(cursor !== null && { cursor }) } },
    }),
  );
}

/** The seven days behind one sheet row (approved leave, UBGT, worked hours). */
export async function fetchTimesheetDays(timesheetId: string): Promise<TimesheetDaysView> {
  const get = platformGraph.GET as unknown as GetTimesheetDays;
  return unwrapEnvelope(
    get("/v1/time/timesheets/{id}/days", { params: { path: { id: timesheetId } } }),
  );
}

/**
 * The same seven days addressed by week instead of by sheet, for weeks that have
 * none. `weekStart` must be a Monday (the route answers 400 otherwise).
 */
export async function fetchWeekDays(
  weekStart: string,
  employeeId: string,
): Promise<TimesheetDaysView> {
  const get = platformGraph.GET as unknown as GetWeekDays;
  return unwrapEnvelope(
    get("/v1/time/timesheet-days", {
      params: { query: { week_start: weekStart, employee_id: employeeId } },
    }),
  );
}

export async function fetchSchedulesPage(
  cursor: string | null,
  status?: ScheduleStatus,
): Promise<KeysetPage<ScheduleRow>> {
  const get = platformGraph.GET as unknown as GetSchedules;
  return unwrapEnvelope(
    get("/v1/time/schedules", {
      params: {
        query: {
          ...(cursor !== null && { cursor }),
          ...(status !== undefined && { status }),
        },
      },
    }),
  );
}

/**
 * SEM-7206. Creation carries NO idempotency key: unlike publish, the route
 * declares no RequiredIdempotencyKeyHeadersSchema, so sending one would claim a
 * replay guarantee the server does not implement.
 */
export async function createSchedule(input: CreateScheduleInput): Promise<ScheduleRow> {
  const post = platformGraph.POST as unknown as PostSchedule;
  const result = await unwrapEnvelope(post("/v1/time/schedules", { body: input }));
  return result.schedule;
}

/**
 * The shift rows of ONE schedule. `schedule_id` is required rather than
 * optional: an unfiltered call would page the whole tenant's roster into a
 * panel that is drawn as a single schedule's contents.
 */
export async function fetchShiftsPage(
  scheduleId: string,
  cursor: string | null,
): Promise<KeysetPage<ShiftRow>> {
  const get = platformGraph.GET as unknown as GetShifts;
  return unwrapEnvelope(
    get("/v1/time/shifts", {
      params: {
        query: { schedule_id: scheduleId, ...(cursor !== null && { cursor }) },
      },
    }),
  );
}

/**
 * The caller's OWN published shifts: pinned to `employeeId` (the shifts list,
 * like the entries list, answers a broad-scope caller with the whole tenant),
 * restricted to published schedules so a draft roster is not announced by the
 * screen before anyone has published it, and to shifts that have not finished
 * unless a single `scheduleId` is asked for. That case is a notification deep
 * link: the person wants to see THAT roster, including days already past if
 * they open it late.
 */
export async function fetchOwnShiftsPage(
  employeeId: string,
  options: { readonly scheduleId: string | null; readonly endsAfter: string },
  cursor: string | null,
): Promise<KeysetPage<ShiftRow>> {
  const get = platformGraph.GET as unknown as GetShifts;
  return unwrapEnvelope(
    get("/v1/time/shifts", {
      params: {
        query: {
          assigned_employee_id: employeeId,
          schedule_status: "published",
          ...(options.scheduleId !== null
            ? { schedule_id: options.scheduleId }
            : { ends_after: options.endsAfter }),
          ...(cursor !== null && { cursor }),
        },
      },
    }),
  );
}

/** See createSchedule for why no idempotency key is sent. */
export async function createShift(input: CreateShiftInput): Promise<ShiftRow> {
  const post = platformGraph.POST as unknown as PostShift;
  const result = await unwrapEnvelope(post("/v1/time/shifts", { body: input }));
  return result.shift;
}

/**
 * One retry writes the WHOLE expansion again — N shift rows, and no route
 * deletes a shift — so the key is required here, unlike createShift. The
 * caller retains it across retries (see publishSchedule).
 */
export async function createShiftPattern(
  scheduleId: string,
  input: ShiftPatternInput,
  idempotencyKey: string,
): Promise<{ pattern: ShiftPatternRow; shift_count: number }> {
  const post = platformGraph.POST as unknown as PostShiftPattern;
  return unwrapEnvelope(
    post("/v1/time/schedules/{id}/shift-patterns", {
      params: { path: { id: scheduleId } },
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/**
 * Schedule publication is intentionally bodyless. The caller retains the key
 * for an ambiguous response; actor, authorization, and lifecycle validation
 * remain entirely server-owned.
 */
export async function publishSchedule(
  id: string,
  idempotencyKey: string,
): Promise<{ schedule: ScheduleRow }> {
  const post = platformGraph.POST as unknown as PostSchedulePublish;
  return unwrapEnvelope(
    post("/v1/time/schedules/{id}/publish", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

// Idempotency-fix-wave Batch 8b (spec 26 api_time_submit_timesheet /
// api_time_approve_timesheet / api_time_reopen_timesheet / api_time_lock_
// timesheet: idempotency: required). All four now hard-require
// x-idempotency-key server-side (services/platform-graph/src/routes/
// time-attendance.ts) — a network retry must never re-run a transition
// (lock is the highest-stakes: it freezes the payroll-eligible entry set
// and emits the overtime breakdown to payroll). Admin PDKS owns the
// approve/reopen/lock key below, and the employee's own timesheet card owns the
// submit key, each for a complete ambiguous-response lifetime.
export async function submitTimesheet(id: string, idempotencyKey: string): Promise<TimesheetRow> {
  const post = platformGraph.POST as unknown as PostTimesheetTransition;
  const result = await unwrapEnvelope(
    post("/v1/time/timesheets/{id}/submit", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.timesheet;
}

export async function approveTimesheet(
  id: string,
  idempotencyKey: string,
): Promise<TimesheetRow> {
  const post = platformGraph.POST as unknown as PostTimesheetTransition;
  const result = await unwrapEnvelope(
    post("/v1/time/timesheets/{id}/approve", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.timesheet;
}

export async function reopenTimesheet(
  id: string,
  idempotencyKey: string,
  reason?: string,
): Promise<TimesheetRow> {
  const post = platformGraph.POST as unknown as PostTimesheetTransition;
  const result = await unwrapEnvelope(
    post("/v1/time/timesheets/{id}/reopen", {
      params: { path: { id } },
      ...(reason !== undefined && { body: { reason } }),
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.timesheet;
}

export async function lockTimesheet(
  id: string,
  idempotencyKey: string,
  jurisdiction?: string,
): Promise<{ timesheet: TimesheetRow; overtime: OvertimePayload }> {
  const post = platformGraph.POST as unknown as PostTimesheetLock;
  return unwrapEnvelope(
    post("/v1/time/timesheets/{id}/lock", {
      params: { path: { id } },
      ...(jurisdiction !== undefined && { body: { jurisdiction } }),
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

/** Safe point summaries and separately authorized, tombstone-aware reason text. */
export interface GeofenceReviewDetail {
  readonly geofence_match: GeofenceSummary | null;
  readonly reviews: readonly { readonly review_id: string; readonly point: GeofencePoint; readonly kind: "worker_reason" | "supervisor_override"; readonly reason: string | null; readonly reason_redacted: boolean; readonly created_at: string }[];
}
export async function fetchGeofenceReview(id: string): Promise<GeofenceReviewDetail> {
  const get = platformGraph.GET as unknown as (path: "/v1/time/entries/{id}/geofence-review", init: { params: { path: { id: string } } }) => Promise<EnvelopedFetchResult<GeofenceReviewDetail>>;
  return unwrapEnvelope(get("/v1/time/entries/{id}/geofence-review", { params: { path: { id } } }));
}
export async function recordGeofenceReview(id: string, kind: "worker_reason" | "supervisor_override", body: { point: GeofencePoint; reason: string; confirmed: true }, idempotencyKey: string): Promise<{ review_id: string }> {
  const post = platformGraph.POST as unknown as (path: "/v1/time/entries/{id}/geofence-reasons" | "/v1/time/entries/{id}/geofence-overrides", init: { params: { path: { id: string } }; body: { point: GeofencePoint; reason: string; confirmed: true }; headers: Record<string, string> }) => Promise<EnvelopedFetchResult<{ review_id: string }>>;
  return unwrapEnvelope(post(kind === "worker_reason" ? "/v1/time/entries/{id}/geofence-reasons" : "/v1/time/entries/{id}/geofence-overrides", { params: { path: { id } }, body, headers: { "x-idempotency-key": idempotencyKey } }));
}
export async function fetchTimeCaller(): Promise<{ employee_id: string | null }> {
  const get = platformGraph.GET as unknown as (path: "/v1/me") => Promise<EnvelopedFetchResult<{ employee_id: string | null }>>;
  return unwrapEnvelope(get("/v1/me"));
}
